import { ApiRequestError } from '@/api/client';
import { http } from '@/api/http';

export const RECURRING_WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const RECURRING_INTERVALS = [
  { weeks: 1, label: 'Every week' },
  { weeks: 2, label: 'Every 2 weeks' },
  { weeks: 4, label: 'Every 4 weeks' },
] as const;

export type RecurringRuleStatus = 0 | 1;

export type RecurringRule = {
  id: string;
  orderId: string;
  intervalWeeks: number;
  dayOfWeek: number;
  serviceTime: string;
  totalTimes: number;
  usedTimes?: number;
  status: RecurringRuleStatus;
  operatorIds: string[];
  operatorNames: string[];
  customerName?: string;
  address?: string;
  nextServiceTime?: string;
};

export type CreateRecurringRuleBody = {
  orderId: string;
  intervalWeeks: number;
  dayOfWeek: number;
  serviceTime: string;
  totalTimes: number;
  operatorIds?: string[];
};

export type UpdateRecurringRuleBody = {
  ruleId: string;
  intervalWeeks?: number;
  dayOfWeek?: number;
  serviceTime?: string;
  totalTimes?: number;
  operatorIds?: string[];
  status?: RecurringRuleStatus;
};

type RecurringHttpResponse = {
  error?: boolean;
  message?: string;
  data?: unknown;
};

const LIST_KEYS = ['list', 'rules', 'items', 'records', 'rows', 'data'] as const;

function readString(o: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
}

function readNumber(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readList(payload: Record<string, unknown>): unknown[] {
  for (const key of LIST_KEYS) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function readIdList(value: unknown): { ids: string[]; names: string[] } {
  if (Array.isArray(value)) {
    const ids: string[] = [];
    const names: string[] = [];
    value.forEach((item) => {
      if (typeof item === 'number' || typeof item === 'string') {
        const id = String(item).trim();
        if (id) ids.push(id);
        return;
      }
      const row = asRecord(item);
      if (!row) return;
      const id = readString(row, ['operator_id', 'operatorId', 'id']);
      const name = readString(row, ['name', 'operator_name', 'operatorName']);
      if (id) ids.push(id);
      if (name) names.push(name);
    });
    return { ids, names };
  }
  if (typeof value === 'string' && value.trim()) {
    return {
      ids: value.split(/[,;|\s]+/).map((part) => part.trim()).filter(Boolean),
      names: [],
    };
  }
  return { ids: [], names: [] };
}

function parseRule(raw: unknown): RecurringRule | null {
  const o = asRecord(raw);
  if (!o) return null;
  const id = readString(o, ['rule_id', 'ruleId', 'id']);
  if (!id) return null;
  const operators = readIdList(
    o.operator_ids ?? o.operatorIds ?? o.operators ?? o.servicers ?? o.servicer_info,
  );
  const nameField = readString(o, ['operator_names', 'operatorNames', 'servicer_names', 'servicerNames']);
  const operatorNames = operators.names.length
    ? operators.names
    : nameField.split(/[,;|]/).map((part) => part.trim()).filter(Boolean);
  const statusRaw = readNumber(o, ['status', 'rule_status', 'ruleStatus']);
  const intervalWeeks = readNumber(o, ['interval_weeks', 'intervalWeeks']) ?? 1;
  const dayOfWeek = readNumber(o, ['day_of_week', 'dayOfWeek']) ?? 0;
  return {
    id,
    orderId: readString(o, ['order_id', 'orderId']),
    intervalWeeks,
    dayOfWeek,
    serviceTime: readString(o, ['service_time', 'serviceTime']) || '09:00',
    totalTimes: readNumber(o, ['total_times', 'totalTimes']) ?? 0,
    usedTimes: readNumber(o, ['used_times', 'usedTimes', 'finished_times', 'finishedTimes', 'done_times', 'doneTimes']),
    status: statusRaw === 0 ? 0 : 1,
    operatorIds: operators.ids,
    operatorNames,
    customerName: readString(o, ['cust_name', 'customer_name', 'customerName', 'name']) || undefined,
    address: readString(o, ['address', 'service_address', 'serviceAddress']) || undefined,
    nextServiceTime: readString(o, ['next_service_time', 'nextServiceTime', 'next_time', 'nextTime']) || undefined,
  };
}

export function parseRecurringRules(data: unknown): RecurringRule[] {
  const rows = Array.isArray(data) ? data : readList(asRecord(data) ?? {});
  return rows.map(parseRule).filter((item): item is RecurringRule => item !== null);
}

export function recurringIntervalLabel(weeks: number): string {
  return RECURRING_INTERVALS.find((item) => item.weeks === weeks)?.label ?? `Every ${weeks} weeks`;
}

export function recurringWeekdayLabel(day: number): string {
  return RECURRING_WEEKDAYS[day] ?? '—';
}

/** 12 小时制钟点，不含 AM/PM。AM 从 08:00 到 11:00，PM 从 12:00 到 07:00。 */
export const RECURRING_AM_TIMES = ['08:00', '09:00', '10:00', '11:00'] as const;
export const RECURRING_PM_TIMES = ['12:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00'] as const;

export function recurringTimesForMeridiem(meridiem: 'AM' | 'PM'): readonly string[] {
  return meridiem === 'PM' ? RECURRING_PM_TIMES : RECURRING_AM_TIMES;
}

function snapRecurringClock(clock: string, meridiem: 'AM' | 'PM'): string {
  const options = recurringTimesForMeridiem(meridiem);
  if (options.includes(clock)) return clock;
  const match = clock.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return options[0];
  const onHour = `${String(Number(match[1])).padStart(2, '0')}:00`;
  return options.includes(onHour) ? onHour : options[0];
}

/** 把订单或规则里的时间拆成 AM/PM 和 12 小时制钟点。 */
export function splitRecurringServiceTime(value: string): { meridiem: 'AM' | 'PM'; clock: string } {
  const raw = value.trim();
  const twelve = raw.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (twelve) {
    const meridiem = twelve[3].toUpperCase() === 'PM' ? 'PM' : 'AM';
    const clock = `${String(Number(twelve[1])).padStart(2, '0')}:${twelve[2]}`;
    return { meridiem, clock: snapRecurringClock(clock, meridiem) };
  }
  const match = raw.match(/(\d{1,2}):(\d{2})/);
  if (!match) return { meridiem: 'AM', clock: '09:00' };
  let hour = Number(match[1]);
  const minute = match[2];
  const meridiem: 'AM' | 'PM' = hour >= 12 ? 'PM' : 'AM';
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  const clock = `${String(hour12).padStart(2, '0')}:${minute}`;
  return { meridiem, clock: snapRecurringClock(clock, meridiem) };
}

/** 12 小时制钟点加 AM/PM，转成接口使用的 24 小时 `HH:mm`。 */
export function joinRecurringServiceTime(clock: string, meridiem: 'AM' | 'PM'): string {
  const match = clock.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return meridiem === 'PM' ? '12:00' : '09:00';
  let hour = Number(match[1]);
  const minute = match[2];
  if (meridiem === 'AM') {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  return `${String(hour).padStart(2, '0')}:${minute}`;
}

export function formatRecurringServiceTime12(value: string): string {
  const { meridiem, clock } = splitRecurringServiceTime(value);
  return `${clock} ${meridiem}`;
}

function assertOk(raw: RecurringHttpResponse, fallback: string): string {
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : fallback);
  }
  return typeof raw.message === 'string' && raw.message.trim() ? raw.message : fallback;
}

/** POST `/system/team/order/recurring/list` */
export async function fetchRecurringRules(status?: RecurringRuleStatus): Promise<RecurringRule[]> {
  const body: Record<string, number> = {};
  if (status === 0 || status === 1) body.status = status;
  const raw = await http.post<RecurringHttpResponse>('/system/team/order/recurring/list', body);
  assertOk(raw, 'Failed to load recurring rules.');
  return parseRecurringRules(raw.data);
}

/** POST `/system/team/order/recurring/create` */
export async function postCreateRecurringRule(
  body: CreateRecurringRuleBody,
): Promise<{ message: string }> {
  const serviceTime = body.serviceTime.trim();
  if (!/^\d{2}:\d{2}$/.test(serviceTime)) {
    throw new ApiRequestError('Service time must use HH:MM.');
  }
  if (!Number.isInteger(body.totalTimes) || body.totalTimes < 1) {
    throw new ApiRequestError('Total visits must be at least 1.');
  }
  const payload: Record<string, string | number> = {
    order_id: body.orderId,
    interval_weeks: body.intervalWeeks,
    day_of_week: body.dayOfWeek,
    service_time: serviceTime,
    total_times: body.totalTimes,
  };
  if (body.operatorIds?.length) payload.operator_ids = body.operatorIds.join(',');
  const raw = await http.post<RecurringHttpResponse>(
    '/system/team/order/recurring/create',
    payload,
    { skipApiEnvelopeModal: true },
  );
  return { message: assertOk(raw, 'Recurring rule created.') };
}

/** POST `/system/team/order/recurring/update` */
export async function postUpdateRecurringRule(
  body: UpdateRecurringRuleBody,
): Promise<{ message: string }> {
  const payload: Record<string, string | number> = { rule_id: body.ruleId };
  if (body.intervalWeeks !== undefined) payload.interval_weeks = body.intervalWeeks;
  if (body.dayOfWeek !== undefined) payload.day_of_week = body.dayOfWeek;
  if (body.serviceTime !== undefined) payload.service_time = body.serviceTime.trim();
  if (body.totalTimes !== undefined) payload.total_times = body.totalTimes;
  if (body.operatorIds !== undefined) payload.operator_ids = body.operatorIds.join(',');
  if (body.status !== undefined) payload.status = body.status;
  const raw = await http.post<RecurringHttpResponse>(
    '/system/team/order/recurring/update',
    payload,
    { skipApiEnvelopeModal: true },
  );
  return { message: assertOk(raw, 'Recurring rule updated.') };
}

/** POST `/system/team/order/recurring/delete` */
export async function postDeleteRecurringRule(ruleId: string): Promise<{ message: string }> {
  const raw = await http.post<RecurringHttpResponse>(
    '/system/team/order/recurring/delete',
    { rule_id: ruleId },
    { skipApiEnvelopeModal: true },
  );
  return { message: assertOk(raw, 'Recurring rule deleted.') };
}
